import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "../../components/AppLayout";
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import axios from "axios";
import TaskListDialog from "../../components/TaskListDialog";
import API_BASE_URL from "../../config/api";

const API_URL = `${API_BASE_URL}/api`;

export default function AdminTaskCalendar() {
  const navigate = useNavigate();
  
  // Check URL params for year/month, otherwise use current date
  const searchParams = new URLSearchParams(window.location.search);
  const urlYear = searchParams.get('year');
  const urlMonth = searchParams.get('month');
  
  const initialDate = (urlYear && urlMonth) 
    ? new Date(parseInt(urlYear), parseInt(urlMonth) - 1, 1)
    : new Date();
  
  const [currentDate, setCurrentDate] = useState(initialDate);
  const [taskCounts, setTaskCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    fetchTaskCounts();
  }, [year, month]);

  const fetchTaskCounts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API_URL}/tasks/calendar/${year}/${month + 1}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.success) {
        // Convert array to map for easy lookup - optimized
        const countsMap = {};
        response.data.taskCounts.forEach((item) => {
          // Extract just the date part without creating Date object
          const dateStr = item.date.split("T")[0];
          countsMap[dateStr] = {
            count: item.count,
            completedCount: item.completedCount || 0,
            assignedCount: item.assignedCount || 0,
            pendingCount: item.pendingCount || 0,
            highPriority: item.highPriority || 0,
            mediumPriority: item.mediumPriority || 0,
            lowPriority: item.lowPriority || 0,
          };
        });
        setTaskCounts(countsMap);
      }
    } catch (error) {
      console.error("❌ Error fetching task counts:", error);
      console.error("Error details:", error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const handleDateClick = (date) => {
    // Format date in local timezone (IST) to match how dates are stored
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;
    
    if (taskCounts[dateStr] && taskCounts[dateStr].count > 0) {
      // Navigate to task details page with the date and current calendar year/month
      const calendarYear = currentDate.getFullYear();
      const calendarMonth = currentDate.getMonth() + 1;
      navigate(`/admin/tasks/${dateStr}?year=${calendarYear}&month=${calendarMonth}`);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedDate(null);
  };

  const getDaysInMonth = () => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = () => {
    return new Date(year, month, 1).getDay();
  };

  const getMonthName = () => {
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    return `${monthNames[month]} ${year}`;
  };

  // Memoize calendar stats for performance
  const calendarStats = useMemo(() => {
    let totalTasks = 0;
    let completedCount = 0;
    let assignedCount = 0;
    let pendingCount = 0;
    let highPriorityCount = 0;
    let mediumPriorityCount = 0;
    let lowPriorityCount = 0;

    Object.values(taskCounts).forEach((data) => {
      totalTasks += data.count;
      completedCount += data.completedCount || 0;
      assignedCount += data.assignedCount || 0;
      pendingCount += data.pendingCount || 0;
      highPriorityCount += data.highPriority;
      mediumPriorityCount += data.mediumPriority;
      lowPriorityCount += data.lowPriority;
    });

    return {
      total: totalTasks,
      completed: completedCount,
      assigned: assignedCount,
      pending: pendingCount,
      high: highPriorityCount,
      medium: mediumPriorityCount,
      low: lowPriorityCount,
    };
  }, [taskCounts]);

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth();
    const firstDay = getFirstDayOfMonth();
    const days = [];

    // Empty cells before first day
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <Box
          key={`empty-${i}`}
          sx={{
            aspectRatio: "1",
            border: "1px solid rgba(196,165,116,0.06)",
            bgcolor: "#3d3d3f",
          }}
        />
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      const taskData = taskCounts[dateStr];
      const hasTask = taskData && taskData.count > 0;

      // Check if today
      const today = new Date();
      const isToday = today.getDate() === day && 
                      today.getMonth() === month && 
                      today.getFullYear() === year;

      // Check if all tasks are completed
      const allTasksCompleted = hasTask && taskData.completedCount === taskData.count;

      // Determine background color based on completion and priority (dark theme, no blues)
      let bgColor = "#2e2e2e";
      if (hasTask) {
        if (allTasksCompleted) {
          bgColor = "#274b2a"; // dark green
        } else if (taskData.highPriority > 0) {
          bgColor = "#3d1f1f"; // dark red
        } else if (taskData.mediumPriority > 0) {
          bgColor = "#4b2f4b"; // dark purple
        } else if (taskData.lowPriority > 0) {
          bgColor = "#4b3a1f"; // dark amber
        }
      }

      days.push(
        <Box
          key={day}
          onClick={() => hasTask && handleDateClick(new Date(year, month, day))}
          sx={{
            aspectRatio: "1",
            border: "1px solid rgba(196,165,116,0.06)",
            bgcolor: bgColor,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            position: "relative",
            p: { xs: 0.25, sm: 0.5, md: 0.75 },
            transition: "all 0.2s",
            cursor: hasTask ? "pointer" : "default",
            minHeight: { xs: 60, sm: 80, md: 100 },
            overflow: "hidden",
            "&:hover": hasTask ? {
              opacity: 0.95,
              transform: "scale(1.02)",
              boxShadow: 2,
            } : {},
          }}
        >
          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              top: { xs: 1, sm: 2, md: 4 },
              left: { xs: 2, sm: 3, md: 6 },
              fontWeight: isToday ? 700 : 500,
              color: "#C4A574",
              fontSize: { xs: '0.6rem', sm: '0.75rem', md: '0.875rem' },
              zIndex: 2
            }}
          >
            {day}
          </Typography>

          {hasTask && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                mt: { xs: 1.5, sm: 2, md: 2.5 },
                width: "100%",
                height: "calc(100% - 20px)",
              }}
            >
              {/* Task count badge */}
              <Box
                sx={{
                  width: { xs: 20, sm: 28, md: 36 },
                  height: { xs: 20, sm: 28, md: 36 },
                  borderRadius: { xs: "4px", sm: "6px", md: "8px" },
                  bgcolor: "#8B7355",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: { xs: 0.25, sm: 0.5 },
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: "white",
                    fontWeight: 700,
                    fontSize: { xs: '0.6rem', sm: '0.8rem', md: '1rem' },
                    lineHeight: 1,
                  }}
                >
                  {taskData.count}
                </Typography>
              </Box>

              {/* Priority indicators */}
              <Box sx={{ 
                display: "flex", 
                gap: { xs: 0.25, sm: 0.5 }, 
                flexWrap: "wrap",
                justifyContent: "center",
                maxWidth: "100%",
                overflow: "hidden"
              }}>
                {taskData.highPriority > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#F44336",
                      fontSize: { xs: '0.4rem', sm: '0.5rem', md: '0.6rem' },
                      fontWeight: 700,
                      lineHeight: 1,
                      whiteSpace: "nowrap"
                    }}
                  >
                    H:{taskData.highPriority}
                  </Typography>
                )}
                {taskData.mediumPriority > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#CE93D8",
                      fontSize: { xs: '0.4rem', sm: '0.5rem', md: '0.6rem' },
                      fontWeight: 700,
                      lineHeight: 1,
                      whiteSpace: "nowrap"
                    }}
                  >
                    M:{taskData.mediumPriority}
                  </Typography>
                )}
                {taskData.lowPriority > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#F57C00",
                      fontSize: { xs: '0.4rem', sm: '0.5rem', md: '0.6rem' },
                      fontWeight: 700,
                      lineHeight: 1,
                      whiteSpace: "nowrap"
                    }}
                  >
                    L:{taskData.lowPriority}
                  </Typography>
                )}
              </Box>
            </Box>
          )}
        </Box>
      );
    }

    return days;
  };

  if (loading) {
    return (
      <AppLayout>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress sx={{ color: "#C4A574" }} />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ p: { xs: 1, sm: 2, md: 4 } }}>
        {/* Header with Navigation */}
        <Box sx={{ textAlign: 'center', mb: { xs: 2, sm: 3 } }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: "#8B7355",
              mb: { xs: 1, sm: 2 },
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
            }}
          >
            Task Calendar
          </Typography>
          
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            justifyContent: "center", 
            gap: { xs: 1, sm: 2 },
            flexWrap: "wrap"
          }}>
            <IconButton
              onClick={handlePreviousMonth}
              sx={{
                bgcolor: "#8B7355",
                color: "white",
                "&:hover": { bgcolor: "#6D5A43" },
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 }
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
            </IconButton>
            
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: "#C4A574", 
                minWidth: { xs: 140, sm: 160, md: 180 }, 
                textAlign: "center",
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }
              }}
            >
              {getMonthName()}
            </Typography>
            
            <IconButton
              onClick={handleNextMonth}
              sx={{
                bgcolor: "#8B7355",
                color: "white",
                "&:hover": { bgcolor: "#6D5A43" },
                width: { xs: 36, sm: 40 },
                height: { xs: 36, sm: 40 }
              }}
            >
              <ChevronRightIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
            </IconButton>
          </Box>
        </Box>

        {/* Stats Summary */}
        <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ mb: { xs: 2, sm: 3 }, justifyContent: "center"}}>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#3d3d3f", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: { xs: 1, sm: 2 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#C4A574",
                    fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.5rem' }
                  }}
                >
                  {calendarStats.total}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#d0cfcf",
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}
                >
                  Total Tasks
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#3a3f3a", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: { xs: 1, sm: 2 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#A5D6A7",
                    fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.5rem' }
                  }}
                >
                  {calendarStats.completed}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#d0cfcf",
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}
                >
                  Completed
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#1f3d3d", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: { xs: 1, sm: 2 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#4FC3F7",
                    fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.5rem' }
                  }}
                >
                  {calendarStats.assigned}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#d0cfcf",
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}
                >
                  Assigned
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <Card sx={{ bgcolor: "#3d3d1f", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: { xs: 1, sm: 2 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#FFD54F",
                    fontSize: { xs: '1.2rem', sm: '1.5rem', md: '1.5rem' }
                  }}
                >
                  {calendarStats.pending}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#d0cfcf",
                    fontSize: { xs: '0.8rem', sm: '0.875rem' }
                  }}
                >
                  Pending
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4} sm={4} md={1.6}>
            <Card sx={{ bgcolor: "#3d1f1f", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: { xs: 1, sm: 2 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#F44336",
                    fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }
                  }}
                >
                  {calendarStats.high}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#d0cfcf",
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}
                >
                  High Priority
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4} sm={4} md={1.6}>
            <Card sx={{ bgcolor: "#3d243d", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: { xs: 1, sm: 2 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#CE93D8",
                    fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }
                  }}
                >
                  {calendarStats.medium}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#d0cfcf",
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}
                >
                  Medium Priority
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={4} sm={4} md={1.6}>
            <Card sx={{ bgcolor: "#3d331f", boxShadow: 2 }}>
              <CardContent sx={{ textAlign: "center", py: { xs: 1, sm: 2 } }}>
                <Typography 
                  variant="h5" 
                  sx={{ 
                    fontWeight: 700, 
                    color: "#F57C00",
                    fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }
                  }}
                >
                  {calendarStats.low}
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: "#d0cfcf",
                    fontSize: { xs: '0.7rem', sm: '0.875rem' }
                  }}
                >
                  Low Priority
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Legend */}
        <Box sx={{ 
          display: "flex", 
          gap: { xs: 1, sm: 2 }, 
          mb: { xs: 1, sm: 2 }, 
          flexWrap: "wrap", 
          justifyContent: "center",
          px: { xs: 1, sm: 0 }
        }}>
          <Chip
            label="H - High Priority (< 7 days)"
            sx={{ 
              bgcolor: "#F44336", 
              color: "white", 
              fontWeight: 600,
              fontSize: { xs: '0.7rem', sm: '0.8125rem' },
              height: { xs: 24, sm: 32 }
            }}
          />
          <Chip
            label="M - Medium Priority (7-15 days)"
            sx={{ 
              bgcolor: "#CE93D8", 
              color: "white", 
              fontWeight: 600,
              fontSize: { xs: '0.7rem', sm: '0.8125rem' },
              height: { xs: 24, sm: 32 }
            }}
          />
          <Chip
            label="L - Low Priority (> 15 days)"
            sx={{ 
              bgcolor: "#F57C00", 
              color: "white", 
              fontWeight: 600,
              fontSize: { xs: '0.7rem', sm: '0.8125rem' },
              height: { xs: 24, sm: 32 }
            }}
          />
        </Box>

        {/* Calendar */}
        <Card sx={{ boxShadow: 3, bgcolor: "#2a2a2a" }}>
          <CardContent sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            {/* Day Headers */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 0,
                mb: { xs: 0.5, sm: 1 },
              }}
            >
              {["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"].map((day) => (
                <Box
                  key={day}
                  sx={{
                    textAlign: "center",
                    py: { xs: 0.5, sm: 1 },
                    bgcolor: "#8B7355",
                    border: "1px solid rgba(196,165,116,0.08)",
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{ 
                      fontWeight: 700, 
                      color: "#ffffff",
                      fontSize: { xs: '0.6rem', sm: '0.75rem', md: '0.875rem' }
                    }}
                  >
                    {day}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* Calendar Grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 0,
              }}
            >
              {renderCalendar()}
            </Box>
          </CardContent>
        </Card>

        {/* Task List Dialog */}
        {selectedDate && (
          <TaskListDialog
            open={dialogOpen}
            onClose={handleCloseDialog}
            selectedDate={selectedDate}
            onTaskUpdated={fetchTaskCounts}
          />
        )}
      </Box>
    </AppLayout>
  );
}